import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../api';
import { useAuth } from '../auth.jsx';
import { Avatar, Badge, DynamicBadge, Modal, ErrorNote, fmtPKR } from '../components/ui.jsx';

const emptyForm = {
  name: '', phone: '', cnic: '', designation: '', departmentId: '',
  employmentTypeId: '', contractEndDate: '',
  siteId: '', dailyRate: '', joinedDate: new Date().toISOString().slice(0, 10),
};

export default function Employees() {
  const { user, can } = useAuth();
  const navigate = useNavigate();
  const isHR = can('employees.manage');

  const [employees, setEmployees] = useState([]);
  const [sites, setSites] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [employmentTypes, setEmploymentTypes] = useState([]);
  const [search, setSearch] = useState('');
  const [dept, setDept] = useState('');
  const [empType, setEmpType] = useState('');
  const [siteId, setSiteId] = useState('');
  const [modal, setModal] = useState(null); // null | 'add' | employee object (edit)
  const [form, setForm] = useState(emptyForm);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const load = () => api('/employees').then(setEmployees).catch((e) => setError(e.message));
  useEffect(() => {
    load();
    api('/sites').then(setSites);
    api('/departments').then(setDepartments);
    api('/employment-types').then(setEmploymentTypes);
  }, []);

  const filtered = useMemo(() => employees.filter((e) => {
    const q = search.toLowerCase();
    return (!q || e.name.toLowerCase().includes(q) || e.code.toLowerCase().includes(q)
        || e.designation.toLowerCase().includes(q))
      && (!dept || e.departmentId === dept)
      && (!empType || e.employmentTypeId === empType)
      && (!siteId || e.siteId === siteId);
  }), [employees, search, dept, empType, siteId]);

  const openAdd = () => {
    setForm({ ...emptyForm, siteId: sites[0]?.id ?? '', departmentId: departments[0]?.id ?? '', employmentTypeId: employmentTypes[0]?.id ?? '' });
    setModal('add'); setError('');
  };
  const openEdit = (emp) => {
    setForm({
      name: emp.name, phone: emp.phone ?? '', cnic: emp.cnic ?? '', designation: emp.designation,
      departmentId: emp.departmentId, siteId: emp.siteId, dailyRate: String(emp.dailyRate),
      employmentTypeId: emp.employmentTypeId, contractEndDate: emp.contractEndDate?.slice(0, 10) ?? '',
      joinedDate: emp.joinedDate.slice(0, 10), status: emp.status,
    });
    setModal(emp); setError('');
  };

  const save = async (e) => {
    e.preventDefault();
    setBusy(true); setError('');
    const body = {
      ...form,
      dailyRate: Number(form.dailyRate),
      contractEndDate: form.contractEndDate || null,
    };
    try {
      if (modal === 'add') await api('/employees', { method: 'POST', body });
      else await api(`/employees/${modal.id}`, { method: 'PATCH', body });
      setModal(null);
      load();
    } catch (err) { setError(err.message); } finally { setBusy(false); }
  };

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  return (
    <>
      <div className="filter-bar">
        <div className="search-box">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8" /><path d="m21 21-4.35-4.35" /></svg>
          <input placeholder="Search name, code, designation…" value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
        <select className="filter-select" value={dept} onChange={(e) => setDept(e.target.value)}>
          <option value="">All Departments</option>
          {departments.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
        </select>
        <select className="filter-select" value={empType} onChange={(e) => setEmpType(e.target.value)}>
          <option value="">All Types</option>
          {employmentTypes.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
        </select>
        {user.role !== 'SUPERVISOR' && (
          <select className="filter-select" value={siteId} onChange={(e) => setSiteId(e.target.value)}>
            <option value="">All Sites</option>
            {sites.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        )}
        {isHR && <button className="btn btn-primary" onClick={openAdd}>+ Add Employee</button>}
      </div>

      <div className="card emp-table-wrap">
        <table>
          <thead>
            <tr>
              <th>Employee</th><th>Code</th><th>Department</th><th>Type</th><th>Site</th>
              <th>Daily Rate</th><th>Status</th>{isHR && <th></th>}
            </tr>
          </thead>
          <tbody>
            {filtered.map((emp) => (
              <tr key={emp.id} onClick={() => navigate(`/employees/${emp.id}`)}>
                <td>
                  <div className="emp-name-cell">
                    <Avatar name={emp.name} id={emp.code} />
                    <div>
                      <div className="emp-name">{emp.name}</div>
                      <div className="emp-desg">{emp.designation}</div>
                    </div>
                  </div>
                </td>
                <td className="text-muted">{emp.code}</td>
                <td><DynamicBadge label={emp.department.name} /></td>
                <td><DynamicBadge label={emp.employmentType.name} /></td>
                <td><span className="site-pill">{emp.site.name}</span></td>
                <td>{fmtPKR(emp.dailyRate)}</td>
                <td><Badge kind={emp.status === 'ACTIVE' ? 'active' : 'absent'}>{emp.status}</Badge></td>
                {isHR && (
                  <td onClick={(e) => e.stopPropagation()}>
                    <button className="btn btn-ghost btn-sm" onClick={() => openEdit(emp)}>Edit</button>
                  </td>
                )}
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr><td colSpan={8}><div className="empty-state"><p>No employees match your filters.</p></div></td></tr>
            )}
          </tbody>
        </table>
      </div>

      <Modal open={!!modal} onClose={() => setModal(null)}
        title={modal === 'add' ? 'Add Employee' : `Edit ${modal?.name ?? ''}`}>
        <form onSubmit={save}>
          <div className="form-row">
            <div className="form-group"><label>Full Name</label>
              <input className="form-input" value={form.name} onChange={set('name')} required /></div>
            <div className="form-group"><label>Designation</label>
              <input className="form-input" value={form.designation} onChange={set('designation')} required /></div>
          </div>
          <div className="form-row">
            <div className="form-group"><label>Department</label>
              <select className="form-select" value={form.departmentId} onChange={set('departmentId')} required>
                <option value="" disabled>Select department…</option>
                {departments.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
              </select></div>
            <div className="form-group"><label>Site</label>
              <select className="form-select" value={form.siteId} onChange={set('siteId')} required>
                <option value="" disabled>Select site…</option>
                {sites.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select></div>
          </div>
          <div className="form-row">
            <div className="form-group"><label>Daily Rate (PKR)</label>
              <input className="form-input" type="number" min="1" value={form.dailyRate} onChange={set('dailyRate')} required /></div>
            <div className="form-group"><label>Date of Joining</label>
              <input className="form-input" type="date" value={form.joinedDate} onChange={set('joinedDate')} required /></div>
          </div>
          <div className="form-row">
            <div className="form-group"><label>Employment Type</label>
              <select className="form-select" value={form.employmentTypeId} onChange={set('employmentTypeId')} required>
                <option value="" disabled>Select type…</option>
                {employmentTypes.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select></div>
            <div className="form-group"><label>Contract End Date <span className="text-muted">(optional)</span></label>
              <input className="form-input" type="date" value={form.contractEndDate} onChange={set('contractEndDate')} /></div>
          </div>
          <div className="form-row">
            <div className="form-group"><label>Phone</label>
              <input className="form-input" value={form.phone} onChange={set('phone')} /></div>
            <div className="form-group"><label>CNIC</label>
              <input className="form-input" value={form.cnic} onChange={set('cnic')} /></div>
          </div>
          {modal !== 'add' && (
            <div className="form-group"><label>Status</label>
              <select className="form-select" value={form.status} onChange={set('status')}>
                {['ACTIVE', 'INACTIVE', 'TERMINATED'].map((s) => <option key={s} value={s}>{s}</option>)}
              </select></div>
          )}
          <ErrorNote error={error} />
          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 8 }}>
            <button type="button" className="btn btn-ghost" onClick={() => setModal(null)}>Cancel</button>
            <button className="btn btn-primary" disabled={busy}>{busy ? 'Saving…' : 'Save Employee'}</button>
          </div>
        </form>
      </Modal>
    </>
  );
}
